import { createFileRoute } from "@tanstack/react-router";
import { useRef } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { LoginPage } from "../pages/Login/LoginPage";
import { useShellHost } from "../shell-handle/ShellHostProvider";
import { createCognitoAuthApi } from "../shell-handle/auth";
import { startEmergencySession } from "../shell-handle/emergencyAuth";
import {
  TIDE_TOKEN_KEY,
  AKVA_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from "../shell-handle/syncedTokens";
import { consumeReturnTo, landingPath } from "../shell-handle/returnTo";
import { broadcastAuthToOtherScreens } from "../shell-handle/desktopAuthSync";
import {
  clearEmergencyMarker,
  matchEmergencyAccount,
  type EmergencyRole,
} from "../exported/shared/utils/emergencySession";

async function syncTokensToLocalStorage() {
  try {
    const s = await fetchAuthSession();
    const id = s.tokens?.idToken?.toString();
    const access = s.tokens?.accessToken?.toString();
    if (id) {
      localStorage.setItem(TIDE_TOKEN_KEY, id);
      localStorage.setItem(AKVA_TOKEN_KEY, id);
    }
    const refresh = access || id;
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  } catch {
    /* not signed in */
  }
}

function LoginRoute() {
  const host = useShellHost();
  const auth = createCognitoAuthApi();
  const emergencyRole = useRef<EmergencyRole | null>(null);
  return (
    <LoginPage
      signIn={async (email, password) => {
        clearEmergencyMarker();
        const role = matchEmergencyAccount(email, password);
        emergencyRole.current = role;
        if (!role) return auth.signIn(email, password);
        await startEmergencySession(role, auth);
        return { isSignedIn: true };
      }}
      confirmSignIn={(response) => auth.confirmSignIn(response)}
      isTotpEnrolmentRequired={async () =>
        emergencyRole.current === null && auth.isTotpEnrolmentRequired()
      }
      startTotpSetup={() => auth.startTotpSetup()}
      completeTotpSetup={(code) => auth.completeTotpSetup(code)}
      abandonSignIn={() => auth.abandonSignIn()}
      resetPassword={(username) => auth.resetPassword(username)}
      confirmResetPassword={(username, code, newPassword) =>
        auth.confirmResetPassword(username, code, newPassword)
      }
      onSuccess={async () => {
        const emergency = emergencyRole.current !== null;
        if (!emergency) await syncTokensToLocalStorage();
        await broadcastAuthToOtherScreens();
        if (!emergency) await host.loadInitialData();
        // Use full navigation so the tide MFE (mounted on non-shell-owned
        // paths via single-spa) takes over and renders the target page.
        // tanstack-router's typed nav has no `/welcome` route; the splat
        // route '/$' catches it at runtime. A captured deep link (returnTo)
        // wins so shared links resolve to their data view after login.
        window.location.assign(landingPath(emergency, consumeReturnTo()));
      }}
    />
  );
}

export const Route = createFileRoute("/login")({
  component: LoginRoute,
});
